import { Component, type ComponentChildren } from 'preact';
import { track } from './lib/analytics';

type Props = { children: ComponentChildren };
type State = { crashed: boolean };

/**
 * Top-level error boundary.
 *
 * A render or lifecycle crash anywhere in the three steps is caught here and
 * replaced with a fallback that keeps the human-contact path open, rather than
 * leaving a white iframe on the marketing site's modal with no way out. That
 * matters more in embed mode than standalone: the footer's phone numbers are not
 * rendered there, so a crash with no fallback strands the customer completely.
 *
 * THE FALLBACK MAY NOT SAY WHAT WAS SENT. It wraps <App/> at the root, so it
 * also catches a crash on the way into step 3, by which point `estimate` has
 * resolved and the lead has already reached n8n and the CRM. In dac-qualifier
 * this read "Nothing you entered was sent anywhere", which was false in exactly
 * that window. Studio inherits the corrected copy and the constraint with it.
 *
 * Studio has NO automated copy gate. dac-qualifier caught its own regressions
 * with a FORBIDDEN_COPY_WORDS test and a dated copy review, neither of which
 * exists in this repo, so the only thing holding this sentence is the comment
 * you are reading and the test below it. The Stage 1 definition of done carries
 * the related bar -- no dollar figure, currency symbol or savings claim anywhere
 * in web/ (ADR-0016) -- and it is likewise unautomated today.
 *
 * The report carries NO parameters. `track()`'s allowlist would strip anything
 * PII-shaped anyway, but an error message is free text originating from a stack
 * that has held the entered address in scope, so none of it is offered to GA4 in
 * the first place. The event's existence and its count are the signal; the
 * detail belongs in Cloud Logging on the functions side.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(): void {
    track('app_exception');
  }

  render() {
    if (!this.state.crashed) return this.props.children;

    return (
      <div class="error-fallback" role="alert">
        <h1>Something went wrong</h1>
        {/* Makes no claim about what was or was not sent. This boundary wraps
            the whole app, so it catches crashes that happen after `estimate`
            has already resolved and the lead is gone. */}
        <p>
          We hit an unexpected problem on our end. Reach a Kasselman Solar advisor and we
          will check the address for you.
        </p>
        <p class="error-fallback-contact">
          <a href="tel:+15184788365">Upstate (518) 478-8365</a>
          <span class="sep" aria-hidden="true">
            ·
          </span>
          <a href="tel:+18456814088">Hudson Valley (845) 681-4088</a>
          <span class="sep" aria-hidden="true">
            ·
          </span>
          <a href="mailto:info@kasselmansolar.com">info@kasselmansolar.com</a>
        </p>
        <button type="button" class="primary" onClick={() => window.location.reload()}>
          Start over
        </button>
      </div>
    );
  }
}
